import { useState, useLayoutEffect, useEffect } from "react";
import useDynamicFormSubmission from "./useDynamicFormSubmission";

const useDynamicFormDoable = ({
  doable,
  configuration,
  initialDocumentUploads,
  postal2FACode,
  saveDraft,
  cancelPendingSave,
}) => {
  // Use the shared form logic hook
  const form = useDynamicFormSubmission(configuration, initialDocumentUploads);
  const {
    responses,
    documentUploads,
    reviewing,
    setReviewing,
    handleValidation,
    buildSubmission,
    buildDraft,
    fieldToFocus,
    focusField,
  } = form;

  // Dashboard-specific state
  const [uploadsInProgress, setUploadsInProgress] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submissionError, setSubmissionError] = useState("");
  const [isDuplicateSubmission, setIsDuplicateSubmission] = useState(false);

  // Focus field when returning from review mode
  useLayoutEffect(() => {
    if (!reviewing && fieldToFocus) {
      focusField(fieldToFocus);
    }
  }, [reviewing, fieldToFocus, focusField]);

  // Scroll to top when entering error, success, or review states
  useEffect(() => {
    if (submissionError || submitted || reviewing) {
      window.scrollTo(0, 0);
    }
  }, [submissionError, submitted, reviewing]);

  // Autosave a draft whenever answers or uploads change. The hook's first
  // call records a baseline instead of saving, so opening a form (or
  // restoring a draft) never creates or rewrites one.
  useEffect(() => {
    if (submitted) return;
    saveDraft(buildDraft());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- saveDraft and buildDraft are recreated every render; responses and documentUploads are the state a draft is built from
  }, [responses, documentUploads, submitted]);

  const handleUploadInProgress = (identifier) => (inProgress) => {
    setUploadsInProgress((prevUploads) => ({
      ...prevUploads,
      [identifier]: inProgress,
    }));
  };

  const submitResponses = async () => {
    const { fieldsSubmission, fieldsToUnset, uploadedDocuments } = buildSubmission();

    const submissionBody = {
      fields: fieldsSubmission,
    };

    if (fieldsToUnset.length > 0) {
      submissionBody.fields_to_unset = fieldsToUnset;
    }

    if (uploadedDocuments.length > 0) {
      submissionBody.uploaded_documents = uploadedDocuments;
    }

    if (configuration.require_postal_2fa_code) {
      submissionBody.postal_2fa_code = postal2FACode;
    }

    // The submission supersedes the draft (the server deletes it on
    // success), so drop any save still waiting on the debounce before the
    // request: a timer firing mid-flight would PUT against a doable the
    // server has just closed. If the submission fails, the server draft is
    // untouched and any later edit queues a fresh save.
    cancelPendingSave();

    try {
      const resp = await fetch(`/api/dashboard/doable/${doable.id}`, {
        method: "PUT",
        credentials: "same-origin",
        body: JSON.stringify(submissionBody),
      });
      if (!!resp.ok) {
        setSubmitted(true);
        setReviewing(false);
      } else {
        if (resp.status === 409) {
          setSubmissionError("Form already submitted");
          setIsDuplicateSubmission(true);
        } else {
          setSubmissionError("Unable to submit form");
        }
        setSubmitted(false);
        setReviewing(false);
      }
    } catch (err) {
      setSubmissionError("Unable to submit form");
      setIsDuplicateSubmission(false);
      setSubmitted(false);
      setReviewing(false);
    }
  };

  const saveResponses = () => {
    if (handleValidation()) {
      submitResponses();
    }
  };

  return {
    ...form,
    submitted,
    submissionError,
    isDuplicateSubmission,
    uploadInProgress: Object.values(uploadsInProgress).some((inProgress) => inProgress),
    handleUploadInProgress,
    submitResponses,
    saveResponses,
  };
};

export default useDynamicFormDoable;
